import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, effect } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AppStateService } from 'src/app/shared/services/helpers/app-state.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { LtmsLoaderComponent } from 'src/app/shared/components/ltms-loader/ltms-loader.component';
import { ChannelSetupService, firstValidationMessage } from 'src/app/shared/services/channel-setup/channel-setup.service';
import { ChannelFilterTiming, ChannelNotifierTiming, emptyFilterTiming } from 'src/app/shared/services/channel-setup/channel-setup.models';
import { HelpersService } from 'src/app/shared/services/helpers/helpers.service';

const TOAST_MS = 15000;

const FILTER_LIMITS = {
  duplicatePreventDelay: { min: 0, max: 2160 },
  expirationDelay: { min: 0, max: 2160 },
  requestCount: { min: 1, max: 99 },
  notificationCount: { min: 1, max: 99 },
  dateLimitInterval: { min: 1, max: 90 },
  dedupFieldIds: { maxLength: 80, maxItems: 10 },
};

const FILTER_KEYS: Array<keyof ChannelFilterTiming> = [
  'duplicate_prevent_delay_hours',
  'single_notification_per_day',
  'request_count',
  'notification_count',
  'dedup_field_ids',
];

const EXPIRATION_KEYS: Array<keyof ChannelFilterTiming> = [
  'expiration_delay_hours',
];

const WINDOW_KEYS: Array<keyof ChannelFilterTiming> = [
  'notifier_timings',
];

const PROCESSING_KEYS: Array<keyof ChannelFilterTiming> = [
  'date_limit_interval',
];

const ON_DEMAND_KEYS: Array<keyof ChannelFilterTiming> = [
  'is_on_demand_trigger_enabled',
];

type SaveSection = 'filters' | 'expiration' | 'timing' | 'processing' | 'onDemand';

type SaveConfirm = {
  which: SaveSection;
  title: string;
  message: string;
};

type HourLine = { lead: string; aside: string };

type FilterTimingForm = {
  duplicatePreventDelay: string;
  singleNotificationPerDay: boolean;
  expirationDelay: string;
  requestCount: string;
  notificationCount: string;
  dedupFieldIds: string;
  notifierTimings: ChannelNotifierTiming[];
  dateLimitInterval: string;
  isOnDemandTriggerEnabled: boolean;
};

@Component({
  selector: 'app-channel-filter-timing',
  templateUrl: './channel-filter-timing.component.html',
  styleUrls: ['./channel-filter-timing.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, LtmsLoaderComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChannelFilterTimingComponent implements OnInit, OnDestroy {
  @Input() isWriteAllowed = false;
  @Output() onDemandEnabled = new EventEmitter<void>();

  brandId: string | null = null;
  loading = false;
  savingFilters = false;
  savingExpiration = false;
  savingTiming = false;
  savingProcessing = false;
  savingOnDemand = false;
  loadError = '';
  form = this.emptyForm();
  draftStart = '';
  draftEnd = '';
  hoursView: 'project' | 'gmt' = 'project';
  zoneName = 'project time';
  defaultHourLines: HourLine[] = [];
  savedHourLines: HourLine[] = [];
  formHourLines: HourLine[] = [];
  private zoneOffset = 0;
  private readonly hoursViewKey = 'channel-setup.hours-view';
  fieldErrors: Record<string, string> = {};
  confirmSave: SaveConfirm | null = null;
  readonly limits = FILTER_LIMITS;
  private saved: ChannelFilterTiming = emptyFilterTiming();
  defaults = {
    notifier_timings: [{ start: '04:30', end: '15:30' }],
    date_limit_interval: 5,
  };

  constructor(
    private readonly appStateService: AppStateService,
    private readonly channelSetupService: ChannelSetupService,
    private readonly helpersService: HelpersService,
    private readonly toastService: ToastService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly host: ElementRef<HTMLElement>,
  ) {
    effect(() => {
      if (!this.appStateService.initialized()) {
        return;
      }
      const authDetails: any = this.appStateService.managerAccess();
      const loggedIn = authDetails?.loggedInManager ?? {};
      const brandDetails: any = this.appStateService.brandDetails();
      this.captureZone(brandDetails);
      this.refreshHourLines();
      const nextBrandId = loggedIn.brand_id || brandDetails?._id || null;
      if (nextBrandId && nextBrandId !== this.brandId) {
        this.brandId = nextBrandId;
        this.load();
      }
      this.changeDetector.markForCheck();
    });
  }

  ngOnInit(): void {
    this.host.nativeElement.addEventListener('click', this.onUnauthorizedClick, true);
    try {
      const stored = localStorage.getItem(this.hoursViewKey);
      if (stored === 'gmt' || stored === 'project') {
        this.hoursView = stored;
      }
    } catch {
      this.hoursView = 'project';
    }
    this.refreshHourLines();
  }

  ngOnDestroy(): void {
    this.host.nativeElement.removeEventListener('click', this.onUnauthorizedClick, true);
  }

  private readonly onUnauthorizedClick = (event: Event): void => {
    if (this.isWriteAllowed) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (!target?.closest?.('.is-unauthorized')) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.toastService.showPopup('ERROR', 'Not authorized', 'Not authorized', TOAST_MS);
  };

  get filtersDirty(): boolean {
    return this.hasDirty(FILTER_KEYS);
  }

  get expirationDirty(): boolean {
    return this.hasDirty(EXPIRATION_KEYS);
  }

  get timingDirty(): boolean {
    return this.hasDirty(WINDOW_KEYS);
  }

  get processingDirty(): boolean {
    return this.hasDirty(PROCESSING_KEYS);
  }

  get onDemandDirty(): boolean {
    return this.hasDirty(ON_DEMAND_KEYS);
  }

  get onDemandReady(): boolean {
    return Boolean(this.saved.on_demand_trigger_ready);
  }

  get onDemandSwitchDisabled(): boolean {
    return !this.isWriteAllowed || (!this.onDemandReady && !this.form.isOnDemandTriggerEnabled);
  }

  get busy(): boolean {
    return this.savingFilters || this.savingExpiration || this.savingTiming || this.savingProcessing || this.savingOnDemand;
  }

  isDirty(key: keyof ChannelFilterTiming): boolean {
    const current = this.toPayload();
    return !this.sameValue(current[key], this.saved[key]);
  }

  hoursAsDaysLabel(value: string | number | null | undefined): string {
    const hours = Math.round(Number(value));
    if (!Number.isFinite(hours) || hours < 0) {
      return '';
    }
    if (hours === 0) {
      return 'Same as 0 days';
    }
    const days = Math.floor(hours / 24);
    const leftover = hours % 24;
    const parts: string[] = [];
    if (days) {
      parts.push(days === 1 ? '1 day' : `${days} days`);
    }
    if (leftover) {
      parts.push(leftover === 1 ? '1 hour' : `${leftover} hours`);
    }
    return `Same as ${parts.join(' ')}`;
  }

  load(): void {
    if (!this.brandId) {
      return;
    }
    this.loading = true;
    this.loadError = '';
    this.changeDetector.markForCheck();
    this.channelSetupService.getFilterTiming(this.brandId).subscribe({
      next: (data) => {
        this.apply(data);
        this.loading = false;
        this.changeDetector.markForCheck();
      },
      error: (error) => {
        this.loading = false;
        this.loadError = firstValidationMessage(error) || 'Could not load filters and timing.';
        this.changeDetector.markForCheck();
      },
    });
  }

  resetFilters(): void {
    this.form = {
      ...this.form,
      duplicatePreventDelay: this.numberText(this.saved.duplicate_prevent_delay_hours),
      singleNotificationPerDay: Boolean(this.saved.single_notification_per_day),
      requestCount: this.numberText(this.saved.request_count),
      notificationCount: this.numberText(this.saved.notification_count),
      dedupFieldIds: Array.isArray(this.saved.dedup_field_ids) ? this.saved.dedup_field_ids.join(',') : '',
    };
    this.clearErrors(FILTER_KEYS);
  }

  resetTiming(): void {
    this.form = {
      ...this.form,
      notifierTimings: (this.saved.notifier_timings || []).map((item) => ({
        start: this.normalizeTime(item.start),
        end: this.normalizeTime(item.end),
      })),
    };
    this.draftStart = '';
    this.draftEnd = '';
    this.clearErrors(WINDOW_KEYS);
    this.refreshHourLines();
  }

  resetProcessing(): void {
    this.form = {
      ...this.form,
      dateLimitInterval: this.numberText(this.saved.date_limit_interval),
    };
    this.clearErrors(PROCESSING_KEYS);
  }

  useDefaultWindows(): void {
    if (!this.isWriteAllowed) {
      return;
    }
    this.form.notifierTimings = [];
    this.draftStart = '';
    this.draftEnd = '';
    this.clearErrors(WINDOW_KEYS);
    this.refreshHourLines();
  }

  useDefaultProcessing(): void {
    if (!this.isWriteAllowed) {
      return;
    }
    this.form.dateLimitInterval = '';
    this.clearErrors(PROCESSING_KEYS);
  }

  get windowsMatchDefault(): boolean {
    return this.form.notifierTimings.length === 0;
  }

  get savedWindowsAreDefault(): boolean {
    return !(this.saved.notifier_timings || []).length;
  }

  get processingMatchesDefault(): boolean {
    return !String(this.form.dateLimitInterval || '').trim();
  }

  get savedProcessingIsDefault(): boolean {
    return this.saved.date_limit_interval == null;
  }

  get savedProcessingDays(): string {
    const days = this.saved.date_limit_interval;
    return days == null ? '—' : String(days);
  }

  setHoursView(view: 'project' | 'gmt'): void {
    if (this.hoursView === view) {
      return;
    }
    this.hoursView = view;
    this.draftStart = '';
    this.draftEnd = '';
    this.refreshHourLines();
    try {
      localStorage.setItem(this.hoursViewKey, view);
    } catch {
      // The choice still applies for this visit.
    }
  }

  private captureZone(brand: any): void {
    const zone = brand?.default_time_zone || {};
    const offset = Number(zone.offset);
    this.zoneName = String(zone.timezone || zone.name || 'project time').trim() || 'project time';
    this.zoneOffset = Number.isFinite(offset) ? offset : 0;
  }

  private refreshHourLines(): void {
    this.defaultHourLines = this.windowDetails(this.defaults.notifier_timings);
    this.savedHourLines = this.windowDetails(this.saved.notifier_timings || []);
    this.formHourLines = this.windowDetails(this.form.notifierTimings);
  }

  private windowDetails(items: Array<{ start: string; end: string }>): HourLine[] {
    return items.map((item) => ({
      lead: this.hoursView === 'gmt' ? this.gmtWindowLabel(item.start, item.end) : this.localWindowLabel(item.start, item.end),
      aside: this.hoursView === 'gmt' ? this.localWindowLabel(item.start, item.end) : this.gmtWindowLabel(item.start, item.end),
    }));
  }

  private localWindowLabel(start: string, end: string): string {
    const startLocal = this.shiftClock(start, this.zoneOffset);
    const endLocal = this.shiftClock(end, this.zoneOffset);
    if (!startLocal || !endLocal) {
      return '';
    }
    return `${this.clockWithDay(startLocal)} – ${this.clockWithDay(endLocal)} ${this.zoneName}`;
  }

  private shiftClock(value: string, offsetMinutes: number): { clock: string; dayOffset: number } | null {
    const minutes = this.clockToMinutes(value);
    if (minutes == null || !Number.isFinite(offsetMinutes)) {
      return null;
    }
    const shifted = this.wrapMinutes(minutes - offsetMinutes);
    return {
      clock: this.minutesToClock(shifted.minutes),
      dayOffset: shifted.dayOffset,
    };
  }

  private wrapMinutes(total: number): { minutes: number; dayOffset: number } {
    if (!Number.isFinite(total)) {
      return { minutes: 0, dayOffset: 0 };
    }
    const dayOffset = Math.floor(total / 1440);
    return { minutes: total - dayOffset * 1440, dayOffset };
  }

  private clockWithDay(value: { clock: string; dayOffset: number }): string {
    if (value.dayOffset > 0) {
      return `${value.clock} next day`;
    }
    if (value.dayOffset < 0) {
      return `${value.clock} previous day`;
    }
    return value.clock;
  }

  private gmtWindowLabel(start: string, end: string): string {
    return `${this.normalizeTime(start)} – ${this.normalizeTime(end)} GMT`;
  }

  resetExpiration(): void {
    this.form = {
      ...this.form,
      expirationDelay: this.numberText(this.saved.expiration_delay_hours),
    };
    this.clearErrors(EXPIRATION_KEYS);
  }

  resetOnDemand(): void {
    this.form = {
      ...this.form,
      isOnDemandTriggerEnabled: Boolean(this.saved.is_on_demand_trigger_enabled),
    };
    this.clearErrors(ON_DEMAND_KEYS);
  }

  saveFilters(): void {
    if (!this.canPrepareSave('filters')) {
      return;
    }
    this.confirmSave = {
      which: 'filters',
      title: 'Save filters?',
      message: 'These filter rules will apply to every invitation and reminder for this project. Continue?',
    };
  }

  saveExpiration(): void {
    if (!this.canPrepareSave('expiration')) {
      return;
    }
    this.confirmSave = {
      which: 'expiration',
      title: 'Save link expiration?',
      message: 'Feedback links stop working after this many hours from request creation. Invitation and backup delays must stay inside this window. Continue?',
    };
  }

  saveTiming(): void {
    if (!this.canPrepareSave('timing')) {
      return;
    }
    this.confirmSave = {
      which: 'timing',
      title: 'Save sending hours?',
      message: this.form.notifierTimings.length
        ? 'Customer Notifier will send only during these project hours.'
        : 'Project hours will be removed. Customer Notifier will use the service hours.',
    };
  }

  saveProcessing(): void {
    if (!this.canPrepareSave('processing')) {
      return;
    }
    this.confirmSave = {
      which: 'processing',
      title: 'Save request processing period?',
      message: String(this.form.dateLimitInterval || '').trim()
        ? 'Customer Notifier will pick up requests from this many past days. Continue?'
        : 'The project period will be removed. Customer Notifier will use the service period.',
    };
  }

  saveOnDemand(): void {
    if (this.form.isOnDemandTriggerEnabled && !this.onDemandReady) {
      this.toastService.showPopup(
        'ERROR',
        'Not ready',
        'Turn on Invites & reminders (Live) and save at least one invitation attempt with a channel first.',
        TOAST_MS,
      );
      return;
    }
    if (!this.canPrepareSave('onDemand')) {
      return;
    }
    const enabling = this.form.isOnDemandTriggerEnabled;
    this.confirmSave = {
      which: 'onDemand',
      title: enabling ? 'Enable on-demand requests?' : 'Turn off on-demand requests?',
      message: enabling
        ? 'Backup Attempt will appear in Notification Setup, and Outbox can trigger extra sends from a request. Continue?'
        : 'Backup Attempt will be hidden, and Outbox on-demand actions will stop. Continue?',
    };
  }

  cancelSaveConfirm(): void {
    this.confirmSave = null;
  }

  confirmSaveNow(): void {
    const which = this.confirmSave?.which;
    this.confirmSave = null;
    if (which === 'filters') {
      this.saveSection(FILTER_KEYS, 'filters', 'Filters saved.');
      return;
    }
    if (which === 'expiration') {
      this.saveSection(EXPIRATION_KEYS, 'expiration', 'Link expiration saved.');
      return;
    }
    if (which === 'timing') {
      this.saveSection(WINDOW_KEYS, 'timing', 'Sending hours saved.');
      return;
    }
    if (which === 'processing') {
      this.saveSection(PROCESSING_KEYS, 'processing', 'Request processing period saved.');
      return;
    }
    if (which === 'onDemand') {
      this.saveSection(
        ON_DEMAND_KEYS,
        'onDemand',
        this.form.isOnDemandTriggerEnabled
          ? 'On-demand is on. Opening Backup Attempt next.'
          : 'On-demand requests are turned off.',
      );
    }
  }

  addTiming(): void {
    if (!this.isWriteAllowed) {
      return;
    }
    const start = this.normalizeTime(this.draftStart);
    const end = this.normalizeTime(this.draftEnd);
    if (this.hoursView === 'gmt') {
      if (!start || !end || start === end) {
        this.toastService.showPopup('ERROR', 'Sending hours', 'Enter a start and end in GMT.', TOAST_MS);
        return;
      }
      if (!this.isStartBeforeEnd(start, end)) {
        this.toastService.showPopup('ERROR', 'Sending hours', 'Start time cannot be later than end time.', TOAST_MS);
        return;
      }
      this.form.notifierTimings = [...this.form.notifierTimings, { start, end }];
      this.draftStart = '';
      this.draftEnd = '';
      this.refreshHourLines();
      return;
    }
    if (!start || !end || start === end) {
      this.toastService.showPopup('ERROR', 'Sending hours', `Enter a start and end in ${this.zoneName}. 12:00 AM is 00:00.`, TOAST_MS);
      return;
    }
    const windows = this.localRangeToGmtWindows(start, end, this.zoneOffset);
    if (!windows.length) {
      this.toastService.showPopup('ERROR', 'Sending hours', 'Those hours could not be converted. Try a shorter range.', TOAST_MS);
      return;
    }
    this.form.notifierTimings = [...this.form.notifierTimings, ...windows];
    this.draftStart = '';
    this.draftEnd = '';
    this.refreshHourLines();
  }

  draftClockNote(): string {
    const start = this.normalizeTime(this.draftStart);
    const end = this.normalizeTime(this.draftEnd);
    if (!start || !end || start === end) {
      return '';
    }
    if (this.hoursView === 'gmt') {
      if (!this.isStartBeforeEnd(start, end)) {
        return '';
      }
      return `Project time: ${this.localWindowLabel(start, end)}`;
    }
    const windows = this.localRangeToGmtWindows(start, end, this.zoneOffset);
    if (!windows.length) {
      return '';
    }
    return `Saved as ${windows.map((item) => `${item.start}–${item.end} GMT`).join(', ')}`;
  }

  private localRangeToGmtWindows(start: string, end: string, offsetMinutes: number): Array<{ start: string; end: string }> {
    const startMin = this.clockToMinutes(start);
    let endMin = this.clockToMinutes(end);
    if (startMin == null || endMin == null || !Number.isFinite(offsetMinutes)) {
      return [];
    }
    if (endMin <= startMin) {
      endMin += 1440;
    }
    const aligned = this.wrapMinutes(startMin + offsetMinutes);
    const gmtStart = aligned.minutes;
    const gmtEnd = endMin + offsetMinutes - aligned.dayOffset * 1440;
    if (gmtEnd <= 1440) {
      return [{ start: this.minutesToClock(gmtStart), end: this.minutesToClock(gmtEnd === 1440 ? 1439 : gmtEnd) }];
    }
    return [
      { start: this.minutesToClock(gmtStart), end: '23:59' },
      { start: '00:00', end: this.minutesToClock(gmtEnd - 1440) },
    ].filter((item) => item.start !== item.end);
  }

  private clockToMinutes(value: string): number | null {
    const normalized = this.normalizeTime(value);
    if (!normalized) {
      return null;
    }
    const [hour, minute] = normalized.split(':').map(Number);
    return hour * 60 + minute;
  }

  private minutesToClock(total: number): string {
    const minutes = ((total % 1440) + 1440) % 1440;
    return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
  }

  removeTiming(index: number): void {
    if (!this.isWriteAllowed) {
      return;
    }
    this.form.notifierTimings = this.form.notifierTimings.filter((_, i) => i !== index);
    this.refreshHourLines();
  }

  private canPrepareSave(which: SaveSection): boolean {
    const errors = this.errorsFor(which);
    this.fieldErrors = { ...this.fieldErrors, ...errors };
    const first = Object.values(errors)[0];
    if (first) {
      this.toastService.showPopup('ERROR', 'Check the form', first, TOAST_MS);
      return false;
    }
    if (!this.hasDirty(this.keysFor(which))) {
      this.toastService.showPopup('INFO', 'No changes', 'No unsaved changes.', TOAST_MS);
      return false;
    }
    return true;
  }

  private saveSection(
    keys: Array<keyof ChannelFilterTiming>,
    which: SaveSection,
    successDetail: string,
  ): void {
    const errors = this.errorsFor(which);
    this.fieldErrors = { ...this.fieldErrors, ...errors };
    const first = Object.values(errors)[0];
    if (first) {
      this.toastService.showPopup('ERROR', 'Check the form', first, TOAST_MS);
      return;
    }
    const dirty = this.dirtyKeys(keys);
    if (!Object.keys(dirty).length) {
      this.toastService.showPopup('INFO', 'No changes', 'No unsaved changes.', TOAST_MS);
      return;
    }
    this.patch(dirty, successDetail, which);
  }

  private keysFor(which: SaveSection): Array<keyof ChannelFilterTiming> {
    if (which === 'filters') {
      return FILTER_KEYS;
    }
    if (which === 'expiration') {
      return EXPIRATION_KEYS;
    }
    if (which === 'timing') {
      return WINDOW_KEYS;
    }
    if (which === 'processing') {
      return PROCESSING_KEYS;
    }
    return ON_DEMAND_KEYS;
  }

  private errorsFor(which: SaveSection): Record<string, string> {
    if (which === 'filters') {
      return {
        ...this.validateField('duplicatePreventDelay'),
        ...this.validateField('requestCount'),
        ...this.validateField('notificationCount'),
        ...this.validateField('dedupFieldIds'),
      };
    }
    if (which === 'expiration') {
      return this.validateField('expirationDelay');
    }
    if (which === 'timing') {
      return this.validateField('notifierTimings');
    }
    if (which === 'processing') {
      return this.validateField('dateLimitInterval');
    }
    return {};
  }

  private patch(
    partial: Partial<ChannelFilterTiming>,
    successDetail: string,
    which: SaveSection,
    onError?: () => void,
  ): void {
    if (!this.isWriteAllowed) {
      this.toastService.showPopup('ERROR', 'Not authorized', 'Not authorized', TOAST_MS);
      onError?.();
      return;
    }
    if (!this.brandId) {
      return;
    }
    this.setBusy(which, true);
    this.channelSetupService.saveFilterTiming(this.brandId, partial).subscribe({
      next: (data) => {
        this.mergeSaved(data, partial);
        if (Object.prototype.hasOwnProperty.call(partial, 'is_on_demand_trigger_enabled')) {
          const enabled = Boolean(data.is_on_demand_trigger_enabled);
          this.syncBrandOnDemandFlag(enabled);
          if (enabled) {
            this.onDemandEnabled.emit();
          }
        }
        this.setBusy(which, false);
        this.toastService.showPopup('SUCCESS', 'Saved', successDetail, TOAST_MS);
        this.changeDetector.markForCheck();
      },
      error: (error) => {
        this.setBusy(which, false);
        onError?.();
        this.toastService.showPopup(
          'ERROR',
          'Could not save',
          firstValidationMessage(error) || 'Error while updating filters and timing.',
          TOAST_MS,
        );
        this.changeDetector.markForCheck();
      },
    });
  }

  private setBusy(which: SaveSection, value: boolean): void {
    if (which === 'filters') {
      this.savingFilters = value;
    }
    if (which === 'expiration') {
      this.savingExpiration = value;
    }
    if (which === 'timing') {
      this.savingTiming = value;
    }
    if (which === 'processing') {
      this.savingProcessing = value;
    }
    if (which === 'onDemand') {
      this.savingOnDemand = value;
    }
  }

  private syncBrandOnDemandFlag(enabled: boolean): void {
    const current: any = this.appStateService.brandDetails() || {};
    const moduleConfiguration = current.module_configuration || {};
    const feedback = moduleConfiguration.feedback || {};
    const feedbackSubmodules = feedback.submodules || {};
    const requests = feedbackSubmodules.requests || {};
    const requestSubmodules = requests.submodules || {};
    const onDemand = requestSubmodules.is_on_demand_trigger_enabled || {};
    this.helpersService.updateBrandDetails({
      ...current,
      module_configuration: {
        ...moduleConfiguration,
        feedback: {
          ...feedback,
          submodules: {
            ...feedbackSubmodules,
            requests: {
              ...requests,
              submodules: {
                ...requestSubmodules,
                is_on_demand_trigger_enabled: {
                  ...onDemand,
                  visible: enabled,
                },
              },
            },
          },
        },
      },
    });
  }

  private apply(data: ChannelFilterTiming): void {
    const next = data ?? emptyFilterTiming();
    this.saved = { ...emptyFilterTiming(), ...next };
    if (next.defaults?.notifier_timings?.length) {
      this.defaults.notifier_timings = next.defaults.notifier_timings.map((item) => ({
        start: this.normalizeTime(item.start),
        end: this.normalizeTime(item.end),
      }));
    }
    if (next.defaults?.date_limit_interval != null) {
      this.defaults.date_limit_interval = Number(next.defaults.date_limit_interval);
    }
    this.form = this.formFromSaved(this.saved);
    this.draftStart = '';
    this.draftEnd = '';
    this.fieldErrors = {};
    this.refreshHourLines();
  }

  private mergeSaved(data: ChannelFilterTiming, partial: Partial<ChannelFilterTiming>): void {
    const keys = Object.keys(partial) as Array<keyof ChannelFilterTiming>;
    this.saved = this.copyKeys(this.saved, data, keys);
    this.form = this.formFromSaved(this.copyKeys(this.toPayload(), data, keys));
    this.clearErrors(keys);
    this.refreshHourLines();
  }

  private copyKeys(
    target: ChannelFilterTiming,
    source: ChannelFilterTiming,
    keys: Array<keyof ChannelFilterTiming>,
  ): ChannelFilterTiming {
    return keys.reduce((next, key) => ({ ...next, [key]: source[key] }), target);
  }

  private formFromSaved(data: ChannelFilterTiming): FilterTimingForm {
    return {
      duplicatePreventDelay: this.numberText(data.duplicate_prevent_delay_hours),
      singleNotificationPerDay: Boolean(data.single_notification_per_day),
      expirationDelay: this.numberText(data.expiration_delay_hours),
      requestCount: this.numberText(data.request_count),
      notificationCount: this.numberText(data.notification_count),
      dedupFieldIds: Array.isArray(data.dedup_field_ids) ? data.dedup_field_ids.join(',') : '',
      notifierTimings: Array.isArray(data.notifier_timings) ? data.notifier_timings.map((item) => ({
        start: this.normalizeTime(item.start),
        end: this.normalizeTime(item.end),
      })).filter((item) => item.start && item.end) : [],
      dateLimitInterval: this.numberText(data.date_limit_interval),
      isOnDemandTriggerEnabled: Boolean(data.is_on_demand_trigger_enabled),
    };
  }

  private toPayload(): ChannelFilterTiming {
    return {
      enable_notifications: this.saved.enable_notifications,
      duplicate_prevent_delay_hours: this.parseNumber(this.form.duplicatePreventDelay),
      single_notification_per_day: this.form.singleNotificationPerDay,
      expiration_delay_hours: this.parseNumber(this.form.expirationDelay),
      request_count: this.parseNumber(this.form.requestCount),
      notification_count: this.parseNumber(this.form.notificationCount),
      dedup_field_ids: this.form.dedupFieldIds.split(',').map((item) => item.trim()).filter(Boolean),
      notifier_timings: this.form.notifierTimings,
      date_limit_interval: this.parseNumber(this.form.dateLimitInterval),
      is_on_demand_trigger_enabled: this.form.isOnDemandTriggerEnabled,
      on_demand_trigger_ready: this.saved.on_demand_trigger_ready,
    };
  }

  private hasDirty(keys: Array<keyof ChannelFilterTiming>): boolean {
    return Object.keys(this.dirtyKeys(keys)).length > 0;
  }

  private dirtyKeys(keys: Array<keyof ChannelFilterTiming>): Partial<ChannelFilterTiming> {
    const current = this.toPayload();
    let dirty: Partial<ChannelFilterTiming> = {};
    keys.forEach((key) => {
      if (!this.sameValue(current[key], this.saved[key])) {
        dirty = { ...dirty, [key]: current[key] };
      }
    });
    const pairTouched = keys.includes('request_count') && keys.includes('notification_count')
      && (Object.prototype.hasOwnProperty.call(dirty, 'request_count')
        || Object.prototype.hasOwnProperty.call(dirty, 'notification_count'));
    if (pairTouched) {
      dirty = {
        ...dirty,
        request_count: current.request_count,
        notification_count: current.notification_count,
      };
    }
    return dirty;
  }

  private clearErrors(keys: Array<keyof ChannelFilterTiming>): void {
    keys.forEach((key) => {
      delete this.fieldErrors[this.errorKeyFor(key)];
    });
  }

  private validateField(errorKey: string): Record<string, string> {
    const errors: Record<string, string> = {};
    if (errorKey === 'duplicatePreventDelay') {
      const message = this.integerRangeError(
        this.form.duplicatePreventDelay,
        FILTER_LIMITS.duplicatePreventDelay,
        `Dedupe period must be a whole number from ${FILTER_LIMITS.duplicatePreventDelay.min} to ${FILTER_LIMITS.duplicatePreventDelay.max} hours.`,
      );
      if (message) {
        errors['duplicatePreventDelay'] = message;
      }
    }
    if (errorKey === 'expirationDelay') {
      const message = this.integerRangeError(
        this.form.expirationDelay,
        FILTER_LIMITS.expirationDelay,
        `Link expiration must be a whole number from ${FILTER_LIMITS.expirationDelay.min} to ${FILTER_LIMITS.expirationDelay.max} hours.`,
      );
      if (message) {
        errors['expirationDelay'] = message;
      }
    }
    if (errorKey === 'requestCount') {
      const message = this.integerRangeError(
        this.form.requestCount,
        FILTER_LIMITS.requestCount,
        `Max requests must be a whole number from ${FILTER_LIMITS.requestCount.min} to ${FILTER_LIMITS.requestCount.max}.`,
        this.isCountPairDirty(),
      );
      if (message) {
        errors['requestCount'] = message;
      }
    }
    if (errorKey === 'notificationCount') {
      const message = this.integerRangeError(
        this.form.notificationCount,
        FILTER_LIMITS.notificationCount,
        `Max notifications must be a whole number from ${FILTER_LIMITS.notificationCount.min} to ${FILTER_LIMITS.notificationCount.max}.`,
        this.isCountPairDirty(),
      );
      if (message) {
        errors['notificationCount'] = message;
      }
    }
    if (errorKey === 'dateLimitInterval') {
      const message = this.integerRangeError(
        this.form.dateLimitInterval,
        FILTER_LIMITS.dateLimitInterval,
        `Request processing period must be a whole number from ${FILTER_LIMITS.dateLimitInterval.min} to ${FILTER_LIMITS.dateLimitInterval.max} days.`,
      );
      if (message) {
        errors['dateLimitInterval'] = message;
      }
    }
    if (errorKey === 'dedupFieldIds') {
      const ids = this.form.dedupFieldIds.split(',').map((item) => item.trim()).filter(Boolean);
      if (ids.length > FILTER_LIMITS.dedupFieldIds.maxItems) {
        errors['dedupFieldIds'] = `Dedupe field accepts at most ${FILTER_LIMITS.dedupFieldIds.maxItems} tag names.`;
      } else if (ids.some((id) => id.length > FILTER_LIMITS.dedupFieldIds.maxLength)) {
        errors['dedupFieldIds'] = `Each dedupe field tag can be at most ${FILTER_LIMITS.dedupFieldIds.maxLength} characters.`;
      }
    }
    if (errorKey === 'notifierTimings' && !this.form.notifierTimings.length) {
      return errors;
    }
    return errors;
  }

  private integerRangeError(
    raw: string,
    limits: { min: number; max: number },
    message: string,
    required = false,
  ): string {
    const value = String(raw ?? '').trim();
    if (!value) {
      return required ? message : '';
    }
    if (!/^-?\d+$/.test(value)) {
      return message;
    }
    const n = Number(value);
    if (!Number.isInteger(n) || n < limits.min || n > limits.max) {
      return message;
    }
    return '';
  }

  private isCountPairDirty(): boolean {
    const current = this.toPayload();
    return !this.sameValue(current.request_count, this.saved.request_count)
      || !this.sameValue(current.notification_count, this.saved.notification_count);
  }

  private errorKeyFor(apiKey: keyof ChannelFilterTiming): string {
    const map: Partial<Record<keyof ChannelFilterTiming, string>> = {
      duplicate_prevent_delay_hours: 'duplicatePreventDelay',
      expiration_delay_hours: 'expirationDelay',
      request_count: 'requestCount',
      notification_count: 'notificationCount',
      date_limit_interval: 'dateLimitInterval',
      notifier_timings: 'notifierTimings',
    };
    return map[apiKey] || String(apiKey);
  }

  private emptyForm(): FilterTimingForm {
    return {
      duplicatePreventDelay: '',
      singleNotificationPerDay: true,
      expirationDelay: '',
      requestCount: '',
      notificationCount: '',
      dedupFieldIds: '',
      notifierTimings: [],
      dateLimitInterval: '',
      isOnDemandTriggerEnabled: false,
    };
  }

  private numberText(value: number | null | undefined): string {
    return value == null || Number.isNaN(Number(value)) ? '' : String(value);
  }

  private parseNumber(value: string): number | null {
    const raw = String(value ?? '').trim();
    if (!raw) {
      return null;
    }
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }

  private normalizeTime(value: string | null | undefined): string {
    const raw = String(value ?? '').trim().replace(/\s*\(GMT\)\s*$/i, '');
    const match = raw.match(/^(\d{1,2}):([0-5]\d)(?::[0-5]\d)?$/);
    if (!match) {
      return '';
    }
    const hour = Number(match[1]);
    if (hour < 0 || hour > 23) {
      return '';
    }
    return `${String(hour).padStart(2, '0')}:${match[2]}`;
  }

  private isStartBeforeEnd(start: string, end: string): boolean {
    const toMin = (clock: string) => {
      const [h, m] = clock.split(':').map(Number);
      return h * 60 + m;
    };
    return toMin(start) <= toMin(end);
  }

  private sameWindows(
    left: Array<{ start: string; end: string }>,
    right: Array<{ start: string; end: string }>,
  ): boolean {
    const a = left.map((item) => `${this.normalizeTime(item.start)}-${this.normalizeTime(item.end)}`).join('|');
    const b = right.map((item) => `${this.normalizeTime(item.start)}-${this.normalizeTime(item.end)}`).join('|');
    return a === b;
  }

  private sameValue(left: unknown, right: unknown): boolean {
    return JSON.stringify(left) === JSON.stringify(right);
  }
}
